import { TApiResponse } from '@/common/types/response';
import { compareLocalizedNamesEnNumericVariant, findSortedInsertIndex } from '@/common/utils/sort';

import {
  TUserGroupListsModalGroupAreas,
  TUserGroupListsModalGroupItemLink,
  TUserGroupListsModalItem,
  TUserGroupListsModalTransportGroup,
  TUserGroupTnvedGroup,
  TUserGroupTnvedItem,
  TUserGroupTnvedIzmUnit,
} from '../../types/user-group-lists-modal';
import { addNodeToTree, deleteNodeById, updateNodeInTree } from '../../utils/fts/user-groups/tree-helpers';
import { TUserGroupsState } from './types';

import { TPageMetadataWithItems } from '@/types/meta';

export function setAreas(state: TUserGroupsState, response: TApiResponse<TUserGroupListsModalGroupAreas[]>) {
  state.areas = response?.data ?? [];
}

export function setCountries(state: TUserGroupsState, response: TApiResponse<TUserGroupListsModalItem[]>) {
  state.countries = response?.data ?? [];
}

export function setAreaCountryLinks(
  state: TUserGroupsState,
  response: TApiResponse<TUserGroupListsModalGroupItemLink[]>,
) {
  state.areaCountryLinks = response?.data ?? [];
}

export function addArea(state: TUserGroupsState, data: TUserGroupListsModalGroupAreas) {
  const { areas } = state;
  const idx = findSortedInsertIndex(areas, data, compareLocalizedNamesEnNumericVariant);
  state.areas = [...areas.slice(0, idx), data, ...areas.slice(idx)];
}

export function updateArea(state: TUserGroupsState, data: TUserGroupListsModalGroupAreas) {
  const index = state.areas.findIndex((a) => a.id === data.id);
  if (index !== -1) {
    state.areas = [...state.areas.slice(0, index), data, ...state.areas.slice(index + 1)];
  }
}

export function deleteArea(state: TUserGroupsState, areaId: string) {
  state.areas = state.areas.filter((a) => a.id !== areaId);
}

export function setLinksForArea(
  state: TUserGroupsState,
  { areaId, links }: { areaId: string; links: TUserGroupListsModalGroupItemLink[] },
) {
  state.areaCountryLinks = state.areaCountryLinks.filter((link) => link.parent !== areaId);
  state.areaCountryLinks.push(...links);
}

export function setCustomsItems(state: TUserGroupsState, response: TApiResponse<TUserGroupListsModalItem[]>) {
  state.customsItems = response?.data ?? [];
}

export function setTransportGroups(
  state: TUserGroupsState,
  response: TApiResponse<TUserGroupListsModalTransportGroup[]>,
) {
  state.transportGroups = response?.data ?? [];
}

export function setTransportItems(state: TUserGroupsState, response: TApiResponse<TUserGroupListsModalItem[]>) {
  state.transportItems = response?.data ?? [];
}

export function setGoodsItems(
  state: TUserGroupsState,
  response: TApiResponse<TPageMetadataWithItems<TUserGroupTnvedItem>>,
) {
  state.goodsItems = response?.data?.items ?? [];
}

export function setGoodsGroups(
  state: TUserGroupsState,
  response: TApiResponse<TPageMetadataWithItems<TUserGroupTnvedGroup>>,
) {
  state.goodsGroups = response?.data?.items ?? [];
}

export function addGoodsGroup(state: TUserGroupsState, group: TUserGroupTnvedGroup) {
  addNodeToTree(state.goodsGroups, group);
  state.goodsGroups = [...state.goodsGroups];
}

export function updateGoodsGroup(state: TUserGroupsState, updatedGroup: TUserGroupTnvedGroup) {
  updateNodeInTree(state.goodsGroups, updatedGroup);
  state.goodsGroups = [...state.goodsGroups];
}

export function deleteGoodsGroup(state: TUserGroupsState, groupId: number) {
  deleteNodeById(state.goodsGroups, groupId);
  state.goodsGroups = [...state.goodsGroups];
}

export function setGoodsUnits(state: TUserGroupsState, response: TApiResponse<TUserGroupTnvedIzmUnit[]>) {
  state.goodsUnits = response?.data ?? [];
}
